import {
    Badge,
    Box,
    Card,
    Group,
    MantineTheme,
    Stack,
    Text,
    useMantineColorScheme,
    useMantineTheme
} from "@mantine/core";

export interface MatchHeaderModel {
    id: string;
    status: MatchStatus;
    inProgress?: boolean; // optional flag for live matches
    result?: "W" | "L" | "D";
    score?: string; // e.g. "2-1"
    homeTeam: string;
    awayTeam: string;
    tournament: string;
    scheduledAt: string; // ISO string
    /** Optional total games in series (best-of). If provided, renders as BO-X under team names. */
    bestOf?: number;
}

type MatchStatus = 'scheduled' | 'completed' | 'live';

function parseSeriesScore(score?: string): { homeWins: number; awayWins: number } | undefined {
    if (!score) return undefined;
    const m = score.match(/^\s*(\d+)\s*[-:]\s*(\d+)\s*$/);
    if (!m) return undefined;
    return {homeWins: parseInt(m[1], 10), awayWins: parseInt(m[2], 10)};
}

function formatDateTime(iso: string) {
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
}

export default function MatchHeaderCard({match}: { match: MatchHeaderModel }) {
    const {colorScheme} = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === "dark";

    // Derived values computed at top (no nested IIFEs)
    const parsed = parseSeriesScore(match.score);
    const homeWins = parsed?.homeWins;
    const awayWins = parsed?.awayWins;
    const completed = match.status === "completed";
    const draw = completed && typeof homeWins === "number" && typeof awayWins === "number" && homeWins === awayWins;
    const homeIsWinner =
        completed && !draw && (match.result === "W" || (typeof homeWins === "number" && typeof awayWins === "number" && homeWins > awayWins));
    const awayIsWinner =
        completed && !draw && (match.result === "L" || (typeof homeWins === "number" && typeof awayWins === "number" && awayWins > homeWins));

    const green = isDark ? "rgba(64, 192, 87, 0.18)" : "rgba(64, 192, 87, 0.12)"; // #40c057
    const red = isDark ? "rgba(250, 82, 82, 0.18)" : "rgba(250, 82, 82, 0.12)"; // #fa5252
    const transparent = "rgba(0,0,0,0)";
    const showGradient = completed && !draw;
    const gradientCss = showGradient
        ? homeIsWinner
            ? `linear-gradient(90deg, ${transparent} -10%, ${green} 10%, ${green} 45%, ${red} 55%, ${red} 90%, ${transparent} 110%)`
            : `linear-gradient(90deg, ${transparent} -10%, ${red} 10%, ${red} 45%, ${green} 55%, ${green} 90%, ${transparent} 110%)`
        : undefined;

    const badgeVariant: 'filled' | 'light' = isDark ? 'filled' : 'light';


    return (
        <Card withBorder radius="md" p="sm" style={{position: "relative", overflow: "hidden"}}>
            {gradientCss && (
                <Box
                    aria-hidden
                    style={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: gradientCss,
                        WebkitMaskImage:
                            "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)",
                        maskImage:
                            "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)",
                        pointerEvents: "none",
                        zIndex: 0,
                    }}
                />
            )}

            <Stack gap={6} style={{position: "relative", zIndex: 1}}>
                {/* Status badge centered on top */}
                <Group justify="center">
                    <MatchStatusBadge badgeVariant={badgeVariant} status={match.status} theme={theme}/>
                </Group>

                {/* Row with large side score badges and centered team names */}
                <Box style={{display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", columnGap: 8}}>
                    <Box style={{justifySelf: "end"}}>
                        <SideScoreBadge
                            score={homeWins}
                            opponentScore={awayWins}
                            matchStatus={match.status}
                            />
                    </Box>

                    {/* Team names only in the center row to align badges precisely */}
                    <Group gap={8} wrap="nowrap" align="center" justify="center">
                        <Text fw={700} c={isDark ? "gray.0" : "dark.7"}>
                            {match.homeTeam}
                        </Text>
                        <Text c={isDark ? undefined : "dimmed"}>vs</Text>
                        <Text fw={700} c={isDark ? "gray.0" : "dark.7"}>
                            {match.awayTeam}
                        </Text>
                    </Group>
                    
                    <Box style={{justifySelf: "start"}}>
                        <SideScoreBadge
                            score={awayWins}
                            opponentScore={homeWins}
                            matchStatus={match.status}
                        />
                    </Box>
                </Box>

                {/* Best-of line under the names, centered and subtle */}
                {typeof match.bestOf === 'number' && match.bestOf > 0 && (
                    <Group justify="center">
                        <Text size="xs" c={isDark ? "gray.4" : "gray.6"}>BO-{match.bestOf}</Text>
                    </Group>
                )}

                {/* Meta info */}
                <Group gap="xs" wrap="wrap" justify="center">
                    <Text size="xs" c={isDark ? undefined : "dimmed"}>
                        Tournament: {match.tournament}
                    </Text>
                    <Text size="xs" c={isDark ? undefined : "dimmed"}>
                        Scheduled: {formatDateTime(match.scheduledAt)}
                    </Text>
                </Group>
            </Stack>
        </Card>
    );
}


function SideScoreBadge({score, opponentScore,  matchStatus}: {
    score?: number;
    opponentScore?: number;
    matchStatus: MatchStatus;
}){
    if(score === undefined || opponentScore === undefined){
        return <></>;
    }
    const isWinning = score > opponentScore;
    const isDraw = score === opponentScore;
    const isFinal = matchStatus === "completed";

    // Ugly way to do this, but it works and is clear
    if(isFinal){
        if(isWinning){
            return <Badge size="lg" color="green" radius="sm">
                {score}
            </Badge>
        }else if(isDraw){
            return <Badge size="lg" color="gray" radius="sm" className="text-gray-900">
                {score}
            </Badge>
        }
        return <Badge size="lg" color="red" radius="sm">
            {score}
        </Badge>
    }
    return <Badge size="lg" color="gray" radius="sm" className="text-gray-900">
        {score}
    </Badge>
}

/**
 * Renders a status badge which will be displayed at the top of the page to indicate the current state of the match.
 * */
function MatchStatusBadge(
    {badgeVariant, status, theme}:
    {
        badgeVariant: 'filled' | 'light';
        status: MatchStatus;
        theme: MantineTheme;
    }
) {

    /**
     * Calculate the badge color based on the status of the match
     * */
    function badgeColor(){
        switch(status){
            case 'live':
                return 'yellow';
            case 'completed':
                return theme.primaryColor;
            case 'scheduled':
                return "grey"
        }
    }
    /**
     * Calculate class names for styling that is specific to certain configurations.
     * For example, live matches use a yellow background, which makes white text hard to read.
     * */
    function classNames(){
        const classNames: string[] = [];
        if(status === 'live'){classNames.push("text-gray-900")}
        return classNames.join(" ");
    }
    /**
     * Calculate the badge text so that we can display more human-readable data.
     * Broken out like this in case we have more complicated text to show based on match state.
     * */
    function badgeText(){
        switch(status){
            case 'live':
                return 'LIVE';
            case 'completed':
                return 'FINAL';
            case 'scheduled':
                return 'LIVE'
        }
    }
    return <Badge variant={badgeVariant} color={badgeColor()} className={classNames()}>
        {badgeText()}
    </Badge>
}
